/**
 * Process Status Constants
 * 
 * Centralized definitions for process statuses used across the application.
 * Backend expects uppercase values, frontend displays user-friendly labels.
 */

export const PROCESS_STATUS = {
  ABERTO: "ABERTO",
  EM_ANDAMENTO: "EM_ANDAMENTO",
  CONCLUIDO: "CONCLUIDO",
  ARQUIVADO: "ARQUIVADO",
} as const;

export type ProcessStatus = typeof PROCESS_STATUS[keyof typeof PROCESS_STATUS];

export interface ProcessStatusConfig {
  value: ProcessStatus;
  label: string;
  color: string;
}

export const PROCESS_STATUS_CONFIG: Record<ProcessStatus, ProcessStatusConfig> = {
  [PROCESS_STATUS.ABERTO]: {
    value: PROCESS_STATUS.ABERTO,
    label: "Aberto",
    color: "bg-green-500/20 text-green-400 border-green-500",
  },
  [PROCESS_STATUS.EM_ANDAMENTO]: {
    value: PROCESS_STATUS.EM_ANDAMENTO,
    label: "Em Andamento",
    color: "bg-blue-500/20 text-blue-400 border-blue-500",
  },
  [PROCESS_STATUS.CONCLUIDO]: {
    value: PROCESS_STATUS.CONCLUIDO,
    label: "Concluído",
    color: "bg-purple-500/20 text-purple-400 border-purple-500",
  },
  [PROCESS_STATUS.ARQUIVADO]: {
    value: PROCESS_STATUS.ARQUIVADO,
    label: "Arquivado",
    color: "bg-slate-500/20 text-slate-400 border-slate-500",
  },
};

/**
 * Get status configuration from status value
 * Handles both backend format (ABERTO) and legacy formats (active, aberto, etc.)
 */
export const getProcessStatusConfig = (status: string): ProcessStatusConfig => {
  if (!status) {
    return PROCESS_STATUS_CONFIG[PROCESS_STATUS.ABERTO];
  }

  const statusUpper = status.toUpperCase();

  // Direct match
  if (statusUpper in PROCESS_STATUS_CONFIG) {
    return PROCESS_STATUS_CONFIG[statusUpper as ProcessStatus];
  }

  // Legacy format mapping
  const legacyMap: Record<string, ProcessStatus> = {
    ACTIVE: PROCESS_STATUS.ABERTO,
    IN_PROGRESS: PROCESS_STATUS.EM_ANDAMENTO,
    COMPLETED: PROCESS_STATUS.CONCLUIDO,
    ARCHIVED: PROCESS_STATUS.ARQUIVADO,
    // Portuguese variants
    ABERTO: PROCESS_STATUS.ABERTO,
    EMANDAMENTO: PROCESS_STATUS.EM_ANDAMENTO,
    EM_ANDAMENTO: PROCESS_STATUS.EM_ANDAMENTO,
    CONCLUIDO: PROCESS_STATUS.CONCLUIDO,
    ARQUIVADO: PROCESS_STATUS.ARQUIVADO,
    // English variants (from old system)
    DRAFT: PROCESS_STATUS.ABERTO,
    OPEN: PROCESS_STATUS.ABERTO,
    SUSPENDED: PROCESS_STATUS.ARQUIVADO,
    CANCELLED: PROCESS_STATUS.ARQUIVADO,
    CANCELED: PROCESS_STATUS.ARQUIVADO,
  };

  const mapped = legacyMap[statusUpper];
  if (mapped) {
    return PROCESS_STATUS_CONFIG[mapped];
  }

  // Default fallback
  return PROCESS_STATUS_CONFIG[PROCESS_STATUS.ABERTO];
};

/**
 * Get status label for display
 */
export const getProcessStatusLabel = (status: string): string => {
  return getProcessStatusConfig(status).label;
};

/**
 * Get status color class for badges/tags
 */
export const getProcessStatusColor = (status: string): string => {
  return getProcessStatusConfig(status).color;
};

/**
 * Get all available statuses for select/dropdown components
 */
export const getProcessStatusOptions = (): ProcessStatusConfig[] => {
  return Object.values(PROCESS_STATUS_CONFIG);
};
